import { useState, useEffect, useCallback } from 'react'
import { ShareLinksApi, CreateShareLinkDto, ShareLinkResponse } from '@/shared/api/shareLinks'
import { useAuth } from '@/modules/auth'

export function useShareLinks(tripId: string) {
  const { getAccessToken } = useAuth()
  const [shareLinks, setShareLinks] = useState<ShareLinkResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const api = new ShareLinksApi(getAccessToken)

  const loadShareLinks = useCallback(async () => {
    if (!tripId) return

    try {
      setIsLoading(true)
      setError(null)
      const links = await api.getShareLinks(tripId)
      setShareLinks(links)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load share links'
      setError(errorMessage)

    } finally {
      setIsLoading(false)
    }
  }, [tripId])

  const createShareLink = async (data: CreateShareLinkDto): Promise<ShareLinkResponse | null> => {
    try {
      setIsLoading(true)
      setError(null)
      const newLink = await api.createShareLink(tripId, data)
      setShareLinks([newLink, ...shareLinks])

      return newLink
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create share link'
      setError(errorMessage)

      return null
    } finally {
      setIsLoading(false)
    }
  }

  const revokeShareLink = async (linkId: string): Promise<boolean> => {
    try {
      setIsLoading(true)
      setError(null)
      await api.revokeShareLink(linkId)
      setShareLinks(shareLinks.filter(l => l.id !== linkId))

      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to revoke share link'
      setError(errorMessage)

      return false
    } finally {
      setIsLoading(false)
    }
  }

  const updateShareLink = async (
    linkId: string,
    data: Partial<CreateShareLinkDto>
  ): Promise<ShareLinkResponse | null> => {
    try {
      setIsLoading(true)
      setError(null)
      const updated = await api.updateShareLink(linkId, data)
      setShareLinks(shareLinks.map(l => l.id === linkId ? updated : l))

      return updated
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update share link'
      setError(errorMessage)

      return null
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (tripId) {
      loadShareLinks()
    }
  }, [tripId, loadShareLinks])

  return {
    shareLinks,
    isLoading,
    error,
    createShareLink,
    revokeShareLink,
    updateShareLink,
    reloadShareLinks: loadShareLinks
  }
}
